import { ExplainerPanel } from "./ExplainerPanel.tsx";
import { theme } from "../theme.ts";

export function DemandExplainer() {
  return (
    <ExplainerPanel summary="How the Demand view is computed — multi-year FY demand per SKU">
      <p style={{ marginTop: 0 }}>
        The Demand view shows <b>per-SKU demand totals for each of the last
        fiscal years plus current FY YTD</b>. Same demand pipeline as the
        Forecast tab (direct + L1 BOM expansion + L2 for fabrics), same{" "}
        <b>LEFT&nbsp;JOIN anti-join</b> (<code>tlc.id IS NULL</code>) against
        double-counting March's FG-with-explicit-component-line invoices.
        Bucketed into fiscal-year buckets (Jul 1 → Jun 30).
      </p>
      <p>
        <b>Single-SKU lookup</b> — type a SKU and press Enter (or click Look
        up). By default it reads from D1's <code>demand_daily</code> cache, so
        results are effectively instant.
      </p>
      <ul>
        <li>
          <b>Live (NetSuite) toggle</b> — flip it on to bypass{" "}
          <code>demand_daily</code> and run the demand SQL straight against
          NetSuite (~2–5s). Current to the minute, and it works for base
          patterns that haven't been backfilled into the cache yet.
        </li>
        <li>
          <b>Month drill-down</b> — click any month row to expand it. That
          fetches (always live from NetSuite) every invoice / credit memo
          behind the month: document #, type, date, customer, attribution
          (Direct line vs. via which FG), and signed qty. The per-transaction
          rows reconcile exactly with the month's Total.
        </li>
      </ul>

      <h4>FY projected — the dashed card</h4>
      <p>
        Every solid card is <b>actuals</b>: invoices that happened. The one
        dashed card is the odd one out — a <b>projection</b> of the current
        fiscal year, which isn't over yet (its actuals card is marked
        <b> · to date</b>).
      </p>
      <p
        style={{
          fontFamily: "'SF Mono',Menlo,Consolas,monospace",
          background: theme.color.surfaceHover,
          padding: "10px 14px",
          borderRadius: 4,
          fontSize: 12.5,
        }}
      >
        CEILING.MATH(FORECAST.LINEAR(thisFY, last 3 FY totals, their years))
      </p>
      <p>
        That is deliberately the <i>same formula the planning workbook uses</i>,
        implemented to match Excel exactly — so the dashboard and the plan file
        agree by construction rather than by coincidence, and either can be used
        to check the other. It fits a straight line through the last three
        <b> complete</b> fiscal years and reads off this one. Shows nothing when
        all three aren't available: two points just draw a line through
        themselves, which isn't a trend.
      </p>
      <div
        style={{
          background: theme.color.warningFaint,
          border: `1px solid ${theme.color.warningBorder}`,
          padding: "10px 14px",
          borderRadius: 8,
          fontSize: 12.5,
          lineHeight: 1.6,
          color: theme.color.warningText,
          margin: "10px 0",
        }}
      >
        <b>A line through three points can't tell a spike from a trend.</b>{" "}
        AAT118A-002 runs 6 → 78 → 9 and projects <b>34</b>, because the single
        78 drags the slope upward. Expect low-volume SKUs to project high, and
        treat the number as "where the trend line lands", not a demand estimate.
      </div>
      <p>
        <b>This is not the Forecast tab's number, and they will not agree.</b>{" "}
        Forecast is seasonal (the same 90-day window across three years × a
        trailing-12-month growth rate) and answers <i>"how much do I order
        now"</i>. This answers <i>"where does the year land on trend"</i>.
        Different questions, different methods.
      </p>

      <div
        style={{
          background: theme.color.brandFaint,
          borderLeft: `3px solid ${theme.color.brand}`,
          padding: "10px 14px",
          margin: "10px 0",
          borderRadius: 4,
          fontSize: 12.5,
          lineHeight: 1.6,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          ⟳ Data refresh schedule (cached path)
        </div>
        <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
          <li>
            <b>Current month</b> — reloaded <b>hourly</b> from NetSuite.
          </li>
          <li>
            <b>Closed months</b> — backfilled once into <code>demand_daily</code>{" "}
            by the enqueue-and-drain backfill (drains on the <b>hourly</b> and{" "}
            <b>every-15-min</b> crons), then effectively immutable.
          </li>
        </ul>
        <div style={{ marginTop: 6, color: theme.color.brandInk }}>
          Need up-to-the-minute numbers regardless? Use the <b>Live</b> toggle —
          it ignores the cache entirely.
        </div>
      </div>
      <ul>
        <li>
          <b>FY label</b> — March's fiscal year, named for the calendar year
          it ends in. FY2025 = Jul 2024 → Jun 2025.
        </li>
        <li>
          <b>Sign convention</b> — invoices add, credit memos subtract, voids
          excluded. <code>posting='T'</code>, <code>mainline='F'</code>,{" "}
          <code>location IN (15, 64)</code>.
        </li>
        <li>
          <b>BOM expansion</b> — direct sales of the SKU + L1 BOM contribution
          from FG sales (and L2 for fabric, via cover assembly). Bases use
          direct-only (no BOM parent).
        </li>
        <li>
          <b>Columns in the monthly breakdown</b> — <b>Sold alone</b> (revenue
          lines for the SKU itself — the only part NetSuite&rsquo;s Sales by
          Item report shows), <b>In umbrella orders</b> ($0 lines for the SKU
          itemised alongside an umbrella that uses it, plus units shipped free),{" "}
          <b>Via umbrella BOM</b> (consumed through an umbrella&rsquo;s bill of
          materials where the SKU wasn&rsquo;t itemised), <b>L2 fabric</b>, and{" "}
          <b>Total used</b>. Every unit is counted once. The first two together
          were previously shown as a single &ldquo;Direct&rdquo; column, which
          made it look like most units were sold on their own — for a frame
          like ALTO908-002 it was 14 of 1,548.
        </li>
      </ul>

      <div
        style={{
          marginTop: 14,
          padding: "10px 14px",
          background: theme.color.warningFaint,
          border: `1px solid ${theme.color.warningBorder}`,
          borderRadius: 8,
          fontSize: 12.5,
          lineHeight: 1.6,
          color: theme.color.warningText,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          ★ Special note — why older cover months sit in “In umbrella orders”
        </div>
        <p style={{ margin: "4px 0" }}>
          <b>
            Before the March-2026 cutover, covers were itemized on the invoice
            alongside their umbrella
          </b>{" "}
          — the finished good (FG) and its cover appeared as two separate lines.
          So a cover shows up <b>both</b> as its own $0 line <b>and</b> as a
          bill-of-materials child of that same FG.
        </p>
        <p style={{ margin: "4px 0" }}>
          To avoid counting it twice, the anti-join keeps only one — it prefers
          the explicit line, so the demand lands in <b>In umbrella orders</b>{" "}
          and <b>Via umbrella BOM shows 0</b>. Sold alone stays small — almost
          none were loose cover sales; they shipped inside umbrellas.
        </p>
        <p style={{ margin: "4px 0" }}>
          <b>At the cutover this flips:</b> invoices stopped itemizing the
          cover, so the same demand now flows through <b>Via umbrella BOM</b>{" "}
          instead (In umbrella orders falls toward 0, Via umbrella BOM rises).
          You’ll see the crossover in early-2026 rows.
        </p>
        <p style={{ margin: "4px 0 0" }}>
          <b>The Total is correct either way</b> — every cover is counted exactly
          once. That split reflects <i>how the invoice was written</i>,
          not a data error, and it does <b>not</b> affect Monitor burn rates or
          the Forecast (both use the Total).
        </p>
      </div>

      <h4 style={{ marginTop: 12 }}>
        Why some cover FY totals look lower than the old artifact
      </h4>
      <p style={{ color: theme.color.textMuted, fontSize: 12 }}>
        Before March 2026, invoices itemized both the FG and its components
        (frame + cover). Both passes would have written to that same
        (SKU, month) key. The LEFT JOIN anti-join keeps only one — otherwise
        every itemized invoice would double-count. So{" "}
        <b>this app's numbers are the deduplicated, correct historical
        demand</b>, matching what the artifact showed after the double-count
        bug fix (v0.11 timeframe). If you're comparing to older artifact
        exports, expect this app to be roughly half for covers in the itemized
        era — and equal in the post-cutover months where the guard is a no-op.
      </p>
    </ExplainerPanel>
  );
}
